import React, { useEffect, useRef, useState } from "react";
import axios from "axios";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import {
  Autocomplete,
  Box,
  InputAdornment,
  MenuItem,
  Select,
  TextField,
  Typography,
} from "../ui";
import {
  getAuthSession,
  BILLNUMBERNAME,
  GETBILLBYREGEX,
  GETSINGLECONFIGS,
  SUCCESS,
  getPreUrlAccordingToCart,
} from "../../constants/constant";
import { snpAxios } from "../global/api";
import uiUtilInstance from "../../utilities/uiUtil";

const BillSearchAutocomplete = ({
  billNumberPrefix,
  onBillSelect,
  size,
  sx,
}) => {
  const [options, setOptions] = useState([]);
  const [inputValue, setInputValue] = useState("");
  const [searchType, setSearchType] = useState("billNo");
  const [resolvedBillNumberPrefix, setResolvedBillNumberPrefix] = useState(
    billNumberPrefix || "Bill-",
  );
  const requestIdRef = useRef(0);
  const navigate = useNavigate();
  const storeActiveShopHash = useSelector(
    (state) => state.setActiveShopHashReducer.activeShopHash,
  );
  const storeApiUrl = useSelector(
    (state) => state.setApiUrlReducer.MAINURL.INITIAL_BASE_URL,
  );

  useEffect(() => {
    setInputValue("");
    setOptions([]);
    requestIdRef.current += 1;
  }, [storeActiveShopHash?.id]);

  useEffect(() => {
    if (billNumberPrefix) {
      setResolvedBillNumberPrefix(billNumberPrefix);
      return;
    }

    const fetchBillNumberPrefix = async () => {
      try {
        const configUrl = `${storeApiUrl.BACKEND_BASE_URL}${getPreUrlAccordingToCart(storeActiveShopHash.cartType)}${GETSINGLECONFIGS}?adminId=${getAuthSession().apiToken}&configName=${BILLNUMBERNAME}&shopHash=${storeActiveShopHash.id}`;
        // This legacy endpoint returns the config value directly (for example,
        // "Bill-") rather than the standard { status, jcJson } response shape.
        const response = await axios.get(configUrl);
        if (response.data) setResolvedBillNumberPrefix(response.data);
      } catch (error) {
        console.error("BillSearchAutocomplete prefix fetch error:", error);
      }
    };

    if (storeActiveShopHash?.id) fetchBillNumberPrefix();
  }, [billNumberPrefix, storeActiveShopHash, storeApiUrl]);

  const fetchBills = async (searchValue) => {
    const requestId = ++requestIdRef.current;
    try {
      const fetchBillUrl = `${storeApiUrl.BACKEND_BASE_URL}${GETBILLBYREGEX}?adminId=${getAuthSession().apiToken}&shopHash=${storeActiveShopHash.id}&regexStr=${encodeURIComponent(searchValue)}`;
      const response = await snpAxios.post(fetchBillUrl);
      if (requestId !== requestIdRef.current) return;
      setOptions(
        response.data.status === SUCCESS
          ? response.data.jcJson.listBills || []
          : [],
      );
    } catch (error) {
      if (requestId === requestIdRef.current) setOptions([]);
      console.error("BillSearchAutocomplete fetchBills error:", error);
    }
  };

  const handleInputChange = (value) => {
    if (document.body.dataset.snpScreenLocked === "true") {
      requestIdRef.current += 1;
      setInputValue("");
      setOptions([]);
      return;
    }

    const valueWithoutPrefix = searchType === "billNo" && value.startsWith(resolvedBillNumberPrefix)
      ? value.slice(resolvedBillNumberPrefix.length)
      : value;
    const numericValue = valueWithoutPrefix.replace(/\D/g, "");
    setInputValue(numericValue);

    const minimumLength = searchType === "mobile" ? 4 : 1;
    if (numericValue.length < minimumLength) {
      requestIdRef.current += 1;
      setOptions([]);
      return;
    }

    fetchBills(
      searchType === "billNo"
        ? `${resolvedBillNumberPrefix}${numericValue}`
        : numericValue,
    );
  };

  const handleSelect = (event, option) => {
    if (!option) return;
    setInputValue("");
    setOptions([]);
    if (onBillSelect) {
      onBillSelect(option);
      return;
    }
    navigate("/dashboard/billentry", {
      state: { tableProps: {}, sBillNo: option.billNo, billType: -1 },
    });
  };

  const handleSearchTypeChange = (event) => {
    requestIdRef.current += 1;
    setSearchType(event.target.value);
    setInputValue("");
    setOptions([]);
  };

  return (
    <Autocomplete
      id="bill-search-autocomplete"
      options={options}
      inputValue={inputValue}
      autoHighlight
      fullWidth
      size={size}
      onChange={handleSelect}
      onInputChange={(event, value, reason) => {
        if (reason === "input") {
          handleInputChange(value);
        } else if (reason === "clear") {
          requestIdRef.current += 1;
          setInputValue("");
          setOptions([]);
        }
      }}
      onBlur={() => {
        requestIdRef.current += 1;
        setOptions([]);
      }}
      getOptionLabel={(option) => {
        if (typeof option === "string") return option;
        return [option?.billNo, option?.custMob].filter(Boolean).join(" ");
      }}
      renderOption={(props, option) => (
        <Box
          component="li"
          key={option.billNo}
          {...props}
          sx={{
            border: "1px solid lightgrey",
            borderRadius: "4px",
            padding: "8px",
            marginBottom: "8px",
          }}
        >
          {uiUtilInstance.getSearchSelectDetails(option)}
        </Box>
      )}
      filterOptions={(billOptions, { inputValue: filterValue }) =>
        billOptions.filter((option) =>
          searchType === "billNo"
            ? option.billNo?.toLowerCase().includes(filterValue.toLowerCase())
            : option.custMob?.toLowerCase().includes(filterValue.toLowerCase()),
        )
      }
      renderInput={(params) => (
        <TextField
          {...params}
          label={searchType === "billNo" ? "Search Bill Number" : "Search Mobile Number"}
          size={size}
          InputProps={{
            ...params.InputProps,
            startAdornment: (
              <InputAdornment position="start" disableTypography>
                <Box
                  sx={{ display: "flex", alignItems: "center", gap: 0.75 }}
                  onKeyDown={(event) => event.stopPropagation()}
                >
                  <Select
                    variant="standard"
                    disableUnderline
                    value={searchType}
                    onChange={handleSearchTypeChange}
                    inputProps={{ "aria-label": "Search by" }}
                    sx={{ minWidth: { xs: 88, sm: 105 }, fontSize: "0.85rem", fontWeight: 600 }}
                  >
                    <MenuItem value="billNo">Bill No.</MenuItem>
                    <MenuItem value="mobile">Mobile</MenuItem>
                  </Select>
                  {searchType === "billNo" && (
                    <Typography component="span" variant="body2" color="text.secondary" sx={{ whiteSpace: "nowrap" }}>
                      {resolvedBillNumberPrefix}
                    </Typography>
                  )}
                </Box>
              </InputAdornment>
            ),
          }}
          inputProps={{
            ...params.inputProps,
            autoComplete: "one-time-code",
            name: "billSearchNumber",
            inputMode: "numeric",
            pattern: "[0-9]*",
          }}
        />
      )}
      sx={sx}
    />
  );
};

export default BillSearchAutocomplete;
